// 📄 useEstatusFacturas.ts
// DEV es la fuente de verdad de parámetros/OT/Personalización Z para los
// pasos que SÍ requieren Orden de Transporte — QAS y PRD heredan esos
// valores en modo lectura y solo registran la confirmación de transporte
// (ver TransportConfirmation). Para los pasos que NO requieren OT, QAS y
// PRD mantienen su propia copia editable e independiente de los parámetros.
import { useState, useEffect } from 'react';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { saveInvoiceStatusConfig, saveTransportConfirmation, saveManualConfigDate, getGeneralsConfig, InvoiceStatusFormData } from '../../../../services/commerce/integrationService.ts';
import { CustomDevelopmentData, emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import { TransportConfirmation, emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';

const ISU_STEP_IDS = ['isu_params', 'isu_bte', 'isu_job'];

const STEP_KEY_MAP: Record<string, string> = {
  isu_params: 'step_params', isu_bte: 'step_bte', isu_job: 'step_job'
};

const DEFAULT_FORM: InvoiceStatusFormData = {
  isuBteEnabled: false, invoiceDocTypeIsu: 'RE', avoidDuplicateIsuPp: false,
  bteProductActive: false, bteProcess: '00005010', bteFunctionModule: 'ARBCIG_BTE_00005010',
  jobName: 'ARBCIG_INVOICESTATUS_EXPORT', jobVariant: '', jobStatus: 'Pendiente',
  requiresOT: false, otNumber: '', comments: '',
};

export function useEstatusFacturas() {
  const { projectId } = useProject();
  const [activeStep, setActiveStep] = useState('isu_params');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [selectedEnv, setSelectedEnv] = useState<'DEV' | 'QAS' | 'PRD'>('DEV');
  const [rawConfig, setRawConfig] = useState<any>(null);

  const [formData, setFormData] = useState<InvoiceStatusFormData>({ ...DEFAULT_FORM });

  const [requiresOTMap, setRequiresOTMap] = useState<Record<string, boolean>>({});
  const [otNumberMap, setOtNumberMap] = useState<Record<string, string>>({});
  const [customDevMap, setCustomDevMap] = useState<Record<string, CustomDevelopmentData>>({});
  const [qasTransportMap, setQasTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdTransportMap, setPrdTransportMap] = useState<Record<string, TransportConfirmation>>({});
  const [qasManualMap, setQasManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [prdManualMap, setPrdManualMap] = useState<Record<string, TransportConfirmation>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [confirmingTransport, setConfirmingTransport] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const handleFieldChange = (field: keyof InvoiceStatusFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    handleFieldChange('requiresOT', requiresOTMap[activeStep] || false);
    handleFieldChange('otNumber', otNumberMap[activeStep] || '');
    handleFieldChange('customDevelopment', customDevMap[activeStep] || emptyCustomDevelopment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, requiresOTMap, otNumberMap, customDevMap]);

  // OT/customDev/transport siempre se leen de DEV (son atributos del paso).
  // Los campos del formulario, en cambio, se leen de DEV solo si el paso
  // requiere OT — si no la requiere, QAS/PRD leen su propia copia.
  const loadEnvironmentData = (configSource = rawConfig) => {
    const devData = configSource?.environments?.DEV;
    if (!devData) {
      setFormData({ ...DEFAULT_FORM });
      setCompletedSteps([]);
      setRequiresOTMap({});
      setOtNumberMap({});
      setCustomDevMap({});
      setQasTransportMap({});
      setPrdTransportMap({});
      setQasManualMap({});
      setPrdManualMap({});
      return;
    }

    if (devData.checklistProgress) {
      const completed = Object.keys(devData.checklistProgress).filter(
        key => devData.checklistProgress[key] === true && ISU_STEP_IDS.includes(key)
      );
      setCompletedSteps(completed);
    } else {
      setCompletedSteps([]);
    }

    const initialFormData: Partial<InvoiceStatusFormData> = {};
    const isu = devData.channels?.invoiceStatus;
    const envChannelData = selectedEnv !== 'DEV' ? configSource?.environments?.[selectedEnv]?.channels?.invoiceStatus : null;
    let currentStepComments = '';
    const qasMap: Record<string, TransportConfirmation> = {};
    const prdMap: Record<string, TransportConfirmation> = {};

    if (isu) {
      if (isu.step_params) {
        if (activeStep === 'isu_params') {
          const requiresOT = isu.step_params.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? isu.step_params : (envChannelData?.step_params || {});
          initialFormData.isuBteEnabled = source.isuBteEnabled || false;
          initialFormData.invoiceDocTypeIsu = source.invoiceDocTypeIsu || 'RE';
          initialFormData.avoidDuplicateIsuPp = source.avoidDuplicateIsuPp || false;
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, isu_params: isu.step_params.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, isu_params: isu.step_params.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, isu_params: isu.step_params.customDevelopment || emptyCustomDevelopment() }));
        qasMap.isu_params = isu.step_params.transport?.QAS || emptyTransportConfirmation();
        prdMap.isu_params = isu.step_params.transport?.PRD || emptyTransportConfirmation();
      }
      if (isu.step_bte) {
        if (activeStep === 'isu_bte') {
          const requiresOT = isu.step_bte.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? isu.step_bte : (envChannelData?.step_bte || {});
          initialFormData.bteProductActive = source.bteProductActive || false;
          initialFormData.bteProcess = source.bteProcess || '00005010';
          initialFormData.bteFunctionModule = source.bteFunctionModule || 'ARBCIG_BTE_00005010';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, isu_bte: isu.step_bte.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, isu_bte: isu.step_bte.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, isu_bte: isu.step_bte.customDevelopment || emptyCustomDevelopment() }));
        qasMap.isu_bte = isu.step_bte.transport?.QAS || emptyTransportConfirmation();
        prdMap.isu_bte = isu.step_bte.transport?.PRD || emptyTransportConfirmation();
      }
      if (isu.step_job) {
        if (activeStep === 'isu_job') {
          const requiresOT = isu.step_job.requiresOT || false;
          const source = (selectedEnv === 'DEV' || requiresOT) ? isu.step_job : (envChannelData?.step_job || {});
          initialFormData.jobName = source.jobName || 'ARBCIG_INVOICESTATUS_EXPORT';
          initialFormData.jobVariant = source.jobVariant || '';
          initialFormData.jobStatus = source.jobStatus || 'Pendiente';
          currentStepComments = source.comments || '';
        }
        setRequiresOTMap(prev => ({ ...prev, isu_job: isu.step_job.requiresOT || false }));
        setOtNumberMap(prev => ({ ...prev, isu_job: isu.step_job.otNumber || '' }));
        setCustomDevMap(prev => ({ ...prev, isu_job: isu.step_job.customDevelopment || emptyCustomDevelopment() }));
        qasMap.isu_job = isu.step_job.transport?.QAS || emptyTransportConfirmation();
        prdMap.isu_job = isu.step_job.transport?.PRD || emptyTransportConfirmation();
      }
    }

    setQasTransportMap(qasMap);
    setPrdTransportMap(prdMap);

    // Estado de "configuración manual" en QAS/PRD — solo relevante para los
    // pasos que NO requieren OT (cada ambiente guarda su propia copia).
    const qasChannelData = configSource?.environments?.QAS?.channels?.invoiceStatus;
    const prdChannelData = configSource?.environments?.PRD?.channels?.invoiceStatus;
    const qasManual: Record<string, TransportConfirmation> = {};
    const prdManual: Record<string, TransportConfirmation> = {};
    ISU_STEP_IDS.forEach((id) => {
      const stepKey = STEP_KEY_MAP[id];
      const qasConfiguredAt = qasChannelData?.[stepKey]?.configuredAt;
      const prdConfiguredAt = prdChannelData?.[stepKey]?.configuredAt;
      qasManual[id] = { transported: !!qasConfiguredAt, transportDate: qasConfiguredAt || '' };
      prdManual[id] = { transported: !!prdConfiguredAt, transportDate: prdConfiguredAt || '' };
    });
    setQasManualMap(qasManual);
    setPrdManualMap(prdManual);

    initialFormData.comments = currentStepComments;
    setFormData(prev => ({ ...prev, ...initialFormData }));
  };

  useEffect(() => {
    if (rawConfig) loadEnvironmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, selectedEnv]);

  useEffect(() => {
    if (!projectId) { setLoadingData(false); return; }
    (async () => {
      try {
        const config = await getGeneralsConfig(projectId);
        if (config) {
          setRawConfig(config);
          loadEnvironmentData(config);
        }
      } catch (error) {
        console.error("Error cargando configuración de Estatus de Facturas:", error);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleEnvChange = (env: 'DEV' | 'QAS' | 'PRD') => {
    setSelectedEnv(env);
    setValidationError(null);
  };

  const handleSave = async () => {
    setValidationError(null);
    setSaveStatus('idle');

    const stepRequiresOT = requiresOTMap[activeStep] || false;
    const targetEnv: 'DEV' | 'QAS' | 'PRD' = (selectedEnv === 'DEV' || stepRequiresOT) ? 'DEV' : selectedEnv;

    if (targetEnv === 'DEV' && formData.requiresOT && !formData.otNumber.trim()) {
      setValidationError("Ingresa el número de la Orden de Transporte."); return;
    }

    if (activeStep === 'isu_params' && !formData.invoiceDocTypeIsu.trim()) {
      setValidationError("El INVOICE_DOC_TYPE es obligatorio."); return;
    }
    if (activeStep === 'isu_bte' && !formData.bteProcess.trim()) {
      setValidationError("El Process BTE es obligatorio."); return;
    }
    if (activeStep === 'isu_job' && !formData.jobName.trim()) {
      setValidationError("El nombre del programa (Job) es obligatorio."); return;
    }

    setSaveStatus('saving');
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");

      const payload = targetEnv === 'DEV'
        ? { ...formData, transport: { QAS: qasTransportMap[activeStep] || emptyTransportConfirmation(), PRD: prdTransportMap[activeStep] || emptyTransportConfirmation() } }
        : formData;
      await saveInvoiceStatusConfig(projectId, targetEnv, activeStep, payload);

      if (targetEnv !== 'DEV') {
        const today = new Date().toISOString().slice(0, 10);
        await saveManualConfigDate(projectId, 'invoiceStatus', STEP_KEY_MAP[activeStep], targetEnv, today);
        const setManualMap = targetEnv === 'QAS' ? setQasManualMap : setPrdManualMap;
        setManualMap(prev => ({ ...prev, [activeStep]: { transported: true, transportDate: today } }));
      }

      const updatedRaw = { ...rawConfig };
      if (!updatedRaw.environments) updatedRaw.environments = {};
      if (!updatedRaw.environments[targetEnv]) updatedRaw.environments[targetEnv] = {};
      if (!updatedRaw.environments[targetEnv].checklistProgress) updatedRaw.environments[targetEnv].checklistProgress = {};
      updatedRaw.environments[targetEnv].checklistProgress[activeStep] = true;

      setRawConfig(updatedRaw);
      if (targetEnv === 'DEV' && !completedSteps.includes(activeStep)) setCompletedSteps([...completedSteps, activeStep]);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    }
  };

  const handleConfirmTransport = async (env: 'QAS' | 'PRD') => {
    setValidationError(null);
    const map = env === 'QAS' ? qasTransportMap : prdTransportMap;
    const current = map[activeStep] || emptyTransportConfirmation();

    if (current.transported && !current.transportDate.trim()) {
      setValidationError(`Ingresa la fecha de pase a ${env}.`); return;
    }

    setConfirmingTransport(true);
    try {
      if (!projectId) throw new Error("Sin proyecto activo.");
      const stepKey = STEP_KEY_MAP[activeStep];
      await saveTransportConfirmation(projectId, 'invoiceStatus', stepKey, env, current);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error(error); setSaveStatus('error');
    } finally {
      setConfirmingTransport(false);
    }
  };

  return {
    activeStep, setActiveStep, completedSteps, formData, handleFieldChange,
    requiresOTMap, setRequiresOTMap, otNumberMap, setOtNumberMap,
    customDevMap, setCustomDevMap,
    qasTransportMap, setQasTransportMap, prdTransportMap, setPrdTransportMap,
    qasManualMap, prdManualMap,
    confirmingTransport, handleConfirmTransport,
    validationError, setValidationError, saveStatus, loadingData,
    handleSave, selectedEnv, handleEnvChange,
  };
}
